/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';

interface AccountFlaggedContextType {
  showModal: () => void;
  hideModal: () => void;
  isModalOpen: boolean;
}

const AccountFlaggedContext = createContext<AccountFlaggedContextType | undefined>(undefined);

interface AccountFlaggedProviderProps {
  children: ReactNode;
}

// Lazy import to avoid circular dependencies
const AccountFlaggedModal = React.lazy(() => import('../components/common/AccountFlaggedModal'));

export const AccountFlaggedProvider: React.FC<AccountFlaggedProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Register the global handler when the component mounts
  useEffect(() => {
    setGlobalAccountFlaggedHandler(showModal);
    return () => setGlobalAccountFlaggedHandler(() => {});
  }, [showModal]);

  return (
    <AccountFlaggedContext.Provider value={{ showModal, hideModal, isModalOpen }}>
      {children}
      <React.Suspense fallback={null}>
        <AccountFlaggedModal isOpen={isModalOpen} onClose={hideModal} />
      </React.Suspense>
    </AccountFlaggedContext.Provider>
  );
};

export const useAccountFlagged = (): AccountFlaggedContextType => {
  const context = useContext(AccountFlaggedContext);
  if (context === undefined) {
    throw new Error('useAccountFlagged must be used within an AccountFlaggedProvider');
  }
  return context;
};

// Global reference for use in non-React contexts (like axios interceptors)
let globalShowAccountFlagged: (() => void) | null = null;

export const setGlobalAccountFlaggedHandler = (handler: () => void) => {
  globalShowAccountFlagged = handler;
};

export const getGlobalAccountFlaggedHandler = () => globalShowAccountFlagged;
